/**
 * 运营后台路由（M1-114）。
 * ★ 全部页面级懒加载：后台首屏只需要登录框，把 Naive UI 的表格/表单分片按需拉，
 *   比 apps/web 更要紧 —— 运营经常在弱网/远程桌面下打开后台。
 * ★ 守卫只判"有没有 token"，不判角色：角色差异由 `can()` 控制入口，
 *   服务端 403 兜底。在前端做路由级 RBAC 会造成"URL 进不去但接口其实能调"的假安全。
 */

import { createRouter, createWebHistory } from 'vue-router';
import { isAuthed, setForcedLogoutHandler } from '@/stores/auth';

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/login', name: 'login', component: () => import('@/views/LoginView.vue'), meta: { public: true } },
    {
      path: '/',
      component: () => import('@/views/ShellView.vue'),
      children: [
        { path: '', redirect: { name: 'dashboard' } },
        { path: 'dashboard', name: 'dashboard', component: () => import('@/views/DashboardView.vue') },
        { path: 'templates', name: 'templates', component: () => import('@/views/TemplatesView.vue') },
        { path: 'orders', name: 'orders', component: () => import('@/views/OrdersView.vue') },
        { path: 'licenses', name: 'licenses', component: () => import('@/views/LicensesView.vue') },
        // ★ B11/C7：人工协助工单队列（授权管理的兜底通道）
        {
          path: 'support-requests',
          name: 'support-requests',
          component: () => import('@/views/SupportRequestsView.vue'),
        },
        { path: 'integrations', name: 'integrations', component: () => import('@/views/IntegrationsView.vue') },
        { path: 'deployments', name: 'deployments', component: () => import('@/views/DeploymentsView.vue') },
        { path: 'backends', name: 'backends', component: () => import('@/views/BackendVersionsView.vue') },
        { path: 'upgrades', name: 'upgrades', component: () => import('@/views/UpgradeBatchesView.vue') },
        { path: 'link-health', name: 'link-health', component: () => import('@/views/LinkHealthView.vue') },
        { path: 'audit-logs', name: 'audit-logs', component: () => import('@/views/AuditLogView.vue') },
        { path: 'categories', name: 'categories', component: () => import('@/views/CategoriesView.vue') },
        { path: 'tags', name: 'tags', component: () => import('@/views/TagsView.vue') },
        { path: 'users', name: 'users', component: () => import('@/views/UsersView.vue') },
        { path: 'configs', name: 'configs', component: () => import('@/views/ConfigsView.vue') },
        { path: 'i18n', name: 'i18n', component: () => import('@/views/I18nView.vue') },
        {
          path: 'account/security',
          name: 'account-security',
          component: () => import('@/views/AccountSecurityView.vue'),
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'notFound',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true },
    },
  ],
});

router.beforeEach((to) => {
  if (to.meta.public) return isAuthed() && to.name === 'login' ? { name: 'dashboard' } : true;
  return isAuthed() ? true : { name: 'login', query: { next: to.fullPath } };
});

/** 会话过期（401）由 api client 触发，这里接上跳转 —— 见 stores/auth.ts 的环形依赖说明 */
setForcedLogoutHandler(() => {
  const current = router.currentRoute.value;
  if (current.name === 'login') return;
  void router.replace({ name: 'login', query: { next: current.fullPath, expired: '1' } });
});
